<template>
    <grid
        :fetchCallback="fetchCallback"
        :columnDefs="columnDefs"
        :maxBlocksInCache="null"
        :suppressScrollOnNewData="true"
        ref="grid"
    >
        <responsive-button
            variant="primary"
            @click="refresh"
            :icon="['fas', 'arrows-rotate']"
            >Refresh</responsive-button
        >
        <responsive-dropdown variant="primary" :items="errorTypes" :icon="['fas', 'filter']">{{
            errorTypeDropdownLabel
        }}</responsive-dropdown>        
    </grid>
</template>

<script>
import debounce from 'lodash/debounce';
import ActionCellRenderer from './action-cell-renderer.vue';
import API from '../../../../api';
import grid from '../../../generic/grid/grid.vue';
import { ADMIN_CHANNEL_ERRORS_NEXTPAX_GRID } from '../../../../endpoints';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../../generic/responsive-dropdown/responsive-dropdown.vue';
import { removeScrollMixin } from '../../../../mixins/remove-scroll-mixin';

export default {
    components: { grid, ResponsiveButton, actionCellRenderer: ActionCellRenderer, ResponsiveDropdown },
    mixins: [removeScrollMixin],
    data() {
        return {
            offset: 0,
            limit: 20,
            sort: 'created',
            desc: false,
            search: '',
            filterByErrorType: '',
        };
    },
    computed: {
        columnDefs() {
            return [
                {
                    field: 'created',
                    headerName: 'Created',
                    type: 'dateTimeColumn',
                    sort: 'desc',
                    minWidth: 190,
                    width: 190,
                },
                {
                    field: 'created_user',
                    headerName: 'User Time',
                    type: 'dateTimeColumn',
                    minWidth: 190,
                    width: 190,
                },
                {
                    field: 'subscriber_id',
                    headerName: 'Subscriber ID',
                    cellRenderer: ({ data }) => {
                        return `<a href="/app/admin/user/details/${data.subscriber_id}">${data.subscriber_id}</a>`;
                    },
                    maxWidth: 140,
                    minWidth: 140,
                },
                {
                    field: 'rental_property_id',
                    headerName: 'Lodgix Property ID',
                    minWidth: 145,
                    width: 145,
                },
                {
                    field: 'nextpax_property_id',
                    headerName: 'NextPax Property ID',
                    minWidth: 145,
                    width: 145,
                },                
                {
                    field: 'from_date',
                    headerName: 'From Date',
                    type: 'dateColumn',
                    minWidth: 120,
                    width: 120,
                },
                {
                    field: 'to_date',
                    headerName: 'To Date',
                    type: 'dateColumn',
                    minWidth: 120,
                    width: 120,
                },
                {
                    field: 'nextpax_channel_display',
                    headerName: 'Channel',
                    minWidth: 110,
                },
                {
                    field: 'nextpax_channel_booking_id',
                    headerName: 'Channel Booking ID',
                    minWidth: 120,
                },                
                {
                    field: 'guest_name',
                    headerName: 'Guest Name',
                    minWidth: 120,
                },
                {
                    field: 'errors',
                    headerName: 'Errors',
                    autoHeight: true,
                    wrapText: true,
                    sortable: false,
                    cellStyle: { whiteSpace: 'normal' },
                    minWidth: 400,
                },                
                {
                    field: '',
                    headerName: '',
                    cellRenderer: 'actionCellRenderer',
                    maxWidth: 100,
                    minWidth: 100,
                    sortable: false,
                },
            ];
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
        
                console.log('Fetching with params:', {
                    offset,
                    limit,
                    sort,
                    desc,
                    search,
                    error_type: this.filterByErrorType,
                });
        
                return API.get(ADMIN_CHANNEL_ERRORS_NEXTPAX_GRID(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        error_type: this.filterByErrorType,
                    },
                })
                    .then(response => {
                        const data = Array.isArray(response?.data) ? response.data : [];
        
                        const processedData = data.map(item => {
                            try {
                                const invoiceSource =  JSON.parse(item.original_request).invoiceSource || '';
                                let nextpax_property_id = null;
                                let nextpax_channel_booking_id = null;

                                if (typeof invoiceSource === 'string' && invoiceSource) {
                                    const lines = invoiceSource.split('\n');
                                    lines.forEach(line => {
                                        if (line.startsWith('Property ID (NextPax):')) {
                                            nextpax_property_id = line.split(':')[1]?.trim() || null;
                                        }
                                        if (line.startsWith('Channel booking number:')) {
                                            nextpax_channel_booking_id = line.split(':')[1]?.trim() || null;
                                        }
                                    });
                                }
        
                                return {
                                    ...item,
                                    nextpax_property_id: nextpax_property_id || item?.nextpax_property_id || null,
                                    nextpax_channel_booking_id: nextpax_channel_booking_id || item?.nextpax_channel_booking_id || null,
                                };
                            } catch (error) {
                                console.error('Error processing item:', item, error);
                                return { ...item }; 
                            }
                        });
        
        
                        return {
                            ...response,
                            data: {
                                ...response.data,
                                data: processedData,
                            },
                        };
                    })
                    .catch(error => {
                        console.error('API fetch error:', error);
                        throw error; // Re-throw to allow upstream error handling
                    });
            };
        },
        errorTypeDropdownLabel() {
            const selectedErrorType = this.errorTypes.find(
                (item) => item.value === this.filterByErrorType
            );
            return selectedErrorType ? selectedErrorType.label : 'All Errors';
        },
        errorTypes() {
            return [
                {
                    label: 'All Errors',
                    value: '',
                    click: () => {
                        this.filterByErrorType = '';
                        this.refresh();
                    },
                },
                {
                    label: 'Not Available Errors',
                    value: 'NOT_AVAILABLE',
                    click: () => {
                        this.filterByErrorType = 'NOT_AVAILABLE';
                        this.refresh();
                    },
                },
            ];
        },
    },
    methods: {
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
    },
};
</script>